"use client"

import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
)

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
))
Label.displayName = LabelPrimitive.Root.displayName

/**
 * FieldLabel — a form field title using the design-system `text-field-title` token (14px medium).
 *
 * Deliberately does NOT reuse `Label`'s base classes: those hardcode `text-sm font-medium
 * leading-none`, and the generated `text-field-title` utility resolves its weight through
 * `var(--tw-font-weight, …)`, so a co-existing `font-medium`/`font-bold` would win and the label
 * would render at the wrong weight. Use this wherever the typography hierarchy matters (section
 * heading → sub heading → field title); use `Label` for plain unstyled-weight labels.
 */
const FieldLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      "text-field-title text-text-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
      className,
    )}
    {...props}
  />
))
FieldLabel.displayName = "FieldLabel"

export { Label, FieldLabel }
